/**
 * OfacOverrideModal — FSR State C false-positive override surface.
 *
 * Bill 2026-05-21 Person Modal Spec-Lock §7.5 ("Yes, I want the OFAC State C
 * FSR-override-with-comp-doc path") + canonical-walkthrough SPEC Addendum 5.2
 * (final paragraph). Supersedes SPEC line 1696's "HARD-BLOCK until Risk Mgmt
 * adjudication" rule — a documented FSR override is now available. Risk Mgmt
 * is still notified (Adaptive Card) but the cart is not hard-blocked while
 * waiting.
 *
 * Surfaces:
 *   - Reason textarea (required, min 20 chars, FSR rationale — audit-visible)
 *   - Compensating document picker — choose from the dialog's existing
 *     attachedDocs collection; FSR uploads the doc via the Documents tab
 *     BEFORE opening this modal. No upload control inside the modal so the
 *     audit row carries a fully-persisted spItemId rather than an in-flight
 *     reference.
 *
 * On submit, fires the `onSubmit({ reason, compDocId })` callback. Parent
 * (OfacScanSection) is responsible for:
 *   1. Persisting `person.extra.verifyClearance.ofac` via `fsrOverride()`.
 *   2. Persisting `person.extra.ofacOverride = { reason, compDocId,
 *      fsrOfficerCode, fsrEntraOid, overriddenAt }` for backwards-compat
 *      consumers that don't yet read the new map.
 *   3. Emitting `verify.section.fsr-override` + `ofac.state-c.override`
 *      SessionEvents via `useEmitEvent`.
 */
import { useMemo, useState } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import type { AttachedDoc } from './attachedDocs';

const REASON_MIN_LEN = 20;

export interface OfacOverrideSubmission {
  readonly reason: string;
  readonly compDocId: string;
  readonly compDocLabel: string;
}

export interface OfacOverrideModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (submission: OfacOverrideSubmission) => void;
  /**
   * Documents currently pinned to the person-in-dialog. Picker lists only
   * documents with a populated `spItemId` (or `docCode` as fallback for
   * CIP-scan-only captures still pre-upload).
   */
  attachedDocs: ReadonlyArray<AttachedDoc>;
  /** Subject's display name — surfaced in the modal header for context. */
  subjectName?: string;
  /** Officer code rendered into the audit-preview chip. */
  fsrOfficerCode?: string;
}

interface DocOption {
  id: string;
  label: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--ink-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontWeight: 600,
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--ink-200)',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 96,
  resize: 'vertical',
};

export function OfacOverrideModal({
  open,
  onClose,
  onSubmit,
  attachedDocs,
  subjectName,
  fsrOfficerCode,
}: OfacOverrideModalProps): JSX.Element | null {
  const [reason, setReason] = useState<string>('');
  const [compDocId, setCompDocId] = useState<string>('');

  const options = useMemo<ReadonlyArray<DocOption>>(() => {
    return attachedDocs.map((d) => ({
      id: d.spItemId ?? d.docCode,
      label: d.filename
        ? `${d.docCode} · ${d.filename}`
        : d.docCode,
    }));
  }, [attachedDocs]);

  const reasonTrimmed = reason.trim();
  const reasonShort = reasonTrimmed.length < REASON_MIN_LEN;
  const canSubmit = !reasonShort && compDocId.length > 0;

  if (!open) return null;

  const selectedOption = options.find((o) => o.id === compDocId);

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    onSubmit({
      reason: reasonTrimmed,
      compDocId,
      compDocLabel: selectedOption?.label ?? compDocId,
    });
    setReason('');
    setCompDocId('');
  };

  const handleCancel = (): void => {
    setReason('');
    setCompDocId('');
    onClose();
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
      <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
        Submit override
      </Button>
    </>
  );

  const title = subjectName
    ? `OFAC false-positive override — ${subjectName}`
    : 'OFAC false-positive override';

  return (
    <ModalShell open={open} onClose={handleCancel} title={title} footer={footer} size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Banner tone="warn" title="Risk Management will be notified">
          Submitting this override does not bypass Risk Management — an Adaptive
          Card is dispatched to the OFAC review queue with your reason + the
          compensating document attached. The cart is no longer hard-blocked
          while review runs; submit + post-commit downgrade are audit-logged.
        </Banner>

        <div>
          <label htmlFor="ofac-override-reason" style={labelStyle}>
            Reason for override (audit-visible · ≥ {REASON_MIN_LEN} chars)
          </label>
          <textarea
            id="ofac-override-reason"
            style={textareaStyle}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this OFAC hit is a false positive (e.g., name match on a delisted historical SDN entry; subject DOB does not match; jurisdiction confirmed via passport)…"
            aria-describedby="ofac-override-reason-counter"
          />
          <div
            id="ofac-override-reason-counter"
            style={{
              marginTop: 4,
              fontSize: 11,
              color: reasonShort ? 'var(--err-text, #b10505)' : 'var(--ink-600)',
            }}
          >
            {reasonTrimmed.length}/{REASON_MIN_LEN} characters
            {reasonShort && ' — too short'}
          </div>
        </div>

        <div>
          <label htmlFor="ofac-override-doc" style={labelStyle}>
            Compensating document
          </label>
          {options.length === 0 ? (
            <Banner tone="info">
              No documents attached to this person yet. Upload the compensating
              document on the Documents tab first, then return here.
            </Banner>
          ) : (
            <select
              id="ofac-override-doc"
              style={inputStyle}
              value={compDocId}
              onChange={(e) => setCompDocId(e.target.value)}
            >
              <option value="">— Select an attached document —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          )}
        </div>

        {fsrOfficerCode && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-700)',
              padding: '6px 10px',
              background: 'var(--ink-50, #f5f5f5)',
              borderRadius: 4,
            }}
          >
            Audit row will record officer code <strong>{fsrOfficerCode}</strong>.
          </div>
        )}
      </div>
    </ModalShell>
  );
}
